import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Mail, X, Paperclip, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { documentEmailApi } from '@/api/documentEmail.api';

interface EmailDocumentDialogProps {
  isOpen:          boolean;
  onClose:         () => void;
  /** Human label of the document, e.g. "GL Journal". */
  documentType:    string;
  documentNo?:     string | null;
  defaultTo?:      string[];
  defaultSubject?: string;
  /** Provided by the preview: rasterises the document to a PDF and returns its base64. */
  capturePdf:      () => Promise<{ base64: string; filename: string } | null>;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Split a free-text recipient field on commas/semicolons/whitespace into trimmed addresses. */
const parseRecipients = (raw: string): string[] =>
  raw.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

/**
 * Emails a transaction document. The PDF is captured client-side (from the preview's paper node)
 * and POSTed to the generic /documents/email endpoint, which sends it via SMTP. Rendered through a
 * portal so it is never included in the html2canvas snapshot of the document.
 */
export const EmailDocumentDialog = ({
  isOpen, onClose, documentType, documentNo, defaultTo, defaultSubject, capturePdf,
}: EmailDocumentDialogProps) => {
  const [to, setTo]           = useState((defaultTo ?? []).join(', '));
  const [cc, setCc]           = useState('');
  const [subject, setSubject] = useState(
    defaultSubject ?? `${documentType}${documentNo ? ` ${documentNo}` : ''}`,
  );
  const [message, setMessage] = useState(
    `Please find attached ${documentType}${documentNo ? ` ${documentNo}` : ''}.`,
  );
  const [attach, setAttach]   = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [sent, setSent]       = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    setError(null);
    const toList = parseRecipients(to);
    const ccList = parseRecipients(cc);
    if (toList.length === 0) { setError('Enter at least one recipient.'); return; }
    const bad = [...toList, ...ccList].find(e => !EMAIL_RE.test(e));
    if (bad) { setError(`"${bad}" is not a valid email address.`); return; }

    setSending(true);
    try {
      let attachmentBase64: string | null = null;
      let attachmentFileName: string | null = null;
      if (attach) {
        const pdf = await capturePdf();
        if (pdf) { attachmentBase64 = pdf.base64; attachmentFileName = pdf.filename; }
      }
      const res = await documentEmailApi.send({
        toRecipients: toList,
        ccRecipients: ccList,
        subject: subject.trim(),
        body: message,
        documentType,
        documentNo: documentNo ?? null,
        attachmentBase64,
        attachmentFileName,
      });
      if (res.data.success) {
        setSent(true);
        setTimeout(onClose, 1400);
      } else {
        setError(res.data.message || 'Failed to send email.');
      }
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to send email. Check the SMTP configuration.');
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    'w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 ' +
    'text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={sending ? undefined : onClose} />
      <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-[#1e3a5f] text-white">
          <span className="flex items-center gap-2 text-sm font-semibold">
            <Mail size={16} /> Email {documentType}{documentNo ? ` — ${documentNo}` : ''}
          </span>
          <button onClick={onClose} disabled={sending} className="p-1 rounded hover:bg-white/15 disabled:opacity-50">
            <X size={16} />
          </button>
        </div>

        {sent ? (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <CheckCircle2 size={40} className="text-emerald-500" />
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Email sent.</p>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-3">
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" /> {error}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">To <span className="text-red-500">*</span></label>
              <input value={to} onChange={e => setTo(e.target.value)} placeholder="name@example.com, another@example.com" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cc</label>
              <input value={cc} onChange={e => setCc(e.target.value)} placeholder="Optional" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Subject</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Message</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} className={inputCls + ' resize-none'} />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input type="checkbox" checked={attach} onChange={e => setAttach(e.target.checked)} className="rounded border-gray-300 dark:border-gray-600" />
              <Paperclip size={14} /> Attach a PDF copy
            </label>
          </div>
        )}

        {!sent && (
          <div className="flex justify-end gap-2 px-5 py-3.5 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-slate-800/60">
            <button onClick={onClose} disabled={sending}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
              Cancel
            </button>
            <button onClick={handleSend} disabled={sending}
              className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-60">
              {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Mail size={14} /> Send</>}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};
